import { c as _c } from "react/compiler-runtime";
import { Button, MantineTheme } from '@mantine/core';
import { ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi';
import { createEl } from 'shared/document';
import { doTo } from 'shared/promise';
import { ChildrenProps, StyleProps, useElementSize } from 'shared/ui';
import { P, match } from 'ts-pattern';
import { twMerge } from 'tailwind-merge';
export const useExpand = () => {
  const $ = _c(5);
  const [expanded, setExpanded] = useState(false);
  let t0;
  if ($[0] !== expanded) {
    t0 = () => setExpanded(!expanded);
    $[0] = expanded;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const toggle = t0;
  let t1;
  if ($[2] !== expanded || $[3] !== toggle) {
    t1 = {
      expanded,
      toggle
    };
    $[2] = expanded;
    $[3] = toggle;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};

/** Will clip the children at maxHeight(if provided) or just allow the container */
export const Expandable = ({
  children,
  className,
  maxHeight,
  showMoreSection,
  hideScroll,
  style,
  onChange,
  disabled,
  theme,
  showMoreOverlay,
  ...props
}: {
  expanded?: boolean;
  /** when true will just display everything and not add expand/collapse functionality */
  disabled?: boolean;
  toggle?: () => void;
  onChange?: (expanded: boolean) => void;
  maxHeight?: string;
  showMoreSection?: ReactNode;
  hideScroll?: boolean;
  theme?: MantineTheme;
  showMoreOverlay?: boolean;
} & ChildrenProps & StyleProps) => {
  const innerUse = useExpand();
  const {
    expanded,
    toggle
  } = {
    expanded: props.expanded ?? innerUse.expanded,
    toggle: props.toggle ?? innerUse.toggle
  };
  const {
    ref: contentRef
  } = useElementSize();
  const {
    ref: maxHeightRef,
    px: maxHeightPx
  } = useUnitToPixels(maxHeight);
  const [layoutState, setLayoutState] = useState<{
    isScrolled: boolean;
    shouldScroll: boolean;
  } | undefined>();
  useLayoutEffect(() => {
    if (!contentRef.current) return;
    const el = contentRef.current;
    const updateLayout = () => {
      setLayoutState({
        isScrolled: el.clientHeight != 0 && el.clientHeight < el.scrollHeight,
        shouldScroll: maxHeightPx != null && el.clientHeight > maxHeightPx + 1
      });
    };
    updateLayout();
    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [contentRef.current, maxHeightPx]);
  const {
    isScrolled,
    shouldScroll
  } = layoutState ?? {};
  const showToggle = maxHeight != null && (isScrolled || expanded && shouldScroll);
  const showFade = maxHeight != null && isScrolled && !expanded;
  return <div ref={maxHeightRef} className={twMerge(`w-full`, className)}>
      {disabled ? <>{children}</> : <div className='relative' ref={contentRef} style={{
      overflowY: maxHeight != null ? hideScroll ? 'hidden' : 'scroll' : undefined,
      maxHeight: expanded ? undefined : maxHeight
    }}>
          {children}
          {showFade && <div style={{
        background: `linear-gradient(to top, ${doTo(style?.backgroundColor ?? 'var(--mantine-color-body)', b => `${b}, `)} transparent)`
      }} className='overlay-div absolute inset-x-0 bottom-0 h-6'></div>}
          {showMoreOverlay && showToggle && !expanded && <div onClick={toggle} className='absolute inset-0 rounded-md bg-transparent transition-colors duration-200 hover:bg-primary/5 cursor-pointer'></div>}
        </div>}
      {(showToggle || showMoreSection) && <div className='flex items-center gap-2'>
          {showToggle && <Button className='!text-gray-400' size='compact-xs' styles={{
        label: {
          fontSize: '1em',
          fontWeight: 'bolder'
        }
      }} leftSection={expanded ? <HiChevronUp /> : <HiChevronDown />} onClick={e => {
        e.stopPropagation();
        toggle();
        onChange?.(!expanded);
      }}>
              {expanded ? 'less' : 'more'}
            </Button>}
          {showMoreSection}
        </div>}
    </div>;
};

// get pixels if it is in that unit, e.g. 22px should return 22
export const getPxFromString = (size: string) => match(/(\d+)px/.exec(size)).with([P._, P.select()], r => parseInt(r)).otherwise(() => undefined);
export const useUnitToPixels = (size: string | undefined) => {
  const ref = useRef<HTMLDivElement | null>(null);
  return useMemo(() => ({
    ref,
    px: size == null ? undefined : getPxFromString(size) ?? measureSize(size, ref.current)
  }), [size, ref.current]);
};
const measureSize = (size: string, element?: HTMLElement | null) => {
  if (typeof document === 'undefined' || !document) return undefined;
  const testEl = createEl('div', {
    class: 'absolute top-0 left-0',
    style: `width: ${size}; visibility: hidden`
  });
  const el = element ?? document.body;
  el.appendChild(testEl);
  const px = testEl.offsetWidth;
  // if (px == 0) {
  //   console.warn(`useUnitToPixels: ${size} is 0`, testEl)
  // }
  el.removeChild(testEl);
  return px;
};